import { useEffect, useMemo, useState } from 'react';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { useFlags } from '@atlaskit/flag';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import DevToolsApiService from 'core/api/dev-tools/dev-tools-api.service';
import {
  DeliveryRequestEmailFormat,
  IDeliveryRequestCatalogue,
  IDeliveryRequestSeedResult,
} from 'core/api/dev-tools/dev-tools-api-interface';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import DemoBatchPanel from './demo-batch-panel';
import ClearRequestsPanel from './clear-requests-panel';
import RequestFieldOverrides from './request-field-overrides';
import DevToolsNav from './dev-tools-nav';

interface IOption<T> {
  label: string;
  value: T;
}

const FORMAT_OPTIONS: IOption<DeliveryRequestEmailFormat>[] = [
  { label: 'v2 — new Delivery Request form', value: 'v2' },
  // Not a delivery request at all: the legacy path books a transport job
  // straight from the email, so nothing appears on the Delivery Requests screen.
  { label: 'legacy — books a transport job directly, no request', value: 'legacy' },
];

const DeliveryRequestTrigger = () => {
  const flags = useFlags();

  const [catalogue, setCatalogue] = useState<IDeliveryRequestCatalogue>();
  const [catalogueError, setCatalogueError] = useState<string>();
  const [scenario, setScenario] = useState<IOption<string> | null>(null);
  const [format, setFormat] = useState<IOption<DeliveryRequestEmailFormat>>(FORMAT_OPTIONS[0]);
  const [count, setCount] = useState('1');
  const [overrides, setOverrides] = useState('');
  // From the dropdowns. The JSON box is applied on top, so it still wins.
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, unknown>>({});
  const [overridesError, setOverridesError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<IDeliveryRequestSeedResult[]>();
  const [wasDryRun, setWasDryRun] = useState(false);

  useEffect(() => {
    DevToolsApiService.getCatalogue()
      .then((data) => {
        setCatalogue(data);
        const first = Object.keys(data.scenarios)[0];
        if (first) setScenario({ label: `${first} — ${data.scenarios[first]}`, value: first });
      })
      .catch((error: Error) => setCatalogueError(error.message));
  }, []);

  const scenarioOptions = useMemo<IOption<string>[]>(
    () =>
      Object.entries(catalogue?.scenarios ?? {}).map(([key, description]) => ({
        label: `${key} — ${description}`,
        value: key,
      })),
    [catalogue],
  );

  const submit = async (dryRun: boolean) => {
    if (!scenario) return;

    let parsedOverrides: Record<string, unknown> = { ...fieldOverrides };
    if (overrides.trim()) {
      try {
        parsedOverrides = { ...parsedOverrides, ...JSON.parse(overrides) };
      } catch (error) {
        setOverridesError(`Invalid JSON: ${(error as Error).message}`);
        return;
      }
    }
    setOverridesError(undefined);
    setSubmitting(true);
    setResults(undefined);

    try {
      const response = await DevToolsApiService.seedDeliveryRequests({
        scenario: scenario.value,
        count: Number(count) || 1,
        format: format.value,
        dryRun,
        overrides: Object.keys(parsedOverrides).length ? parsedOverrides : undefined,
      });
      setResults(response.results);
      setWasDryRun(response.dryRun);
      if (!dryRun) {
        const accepted = response.results.filter((result) => result.status === 200).length;
        showSuccessFlag(
          'Requests sent',
          `${accepted} of ${response.results.length} accepted by the inbound handler.`,
          flags,
        );
      }
    } catch (error) {
      showErrorFlag('Trigger failed', (error as Error).message, flags);
    } finally {
      setSubmitting(false);
    }
  };

  if (catalogueError) {
    return (
      <>
        <PageHeader title='Delivery Request Trigger' />
        <div className='mt-6 rounded border border-red-200 bg-red-50 p-4'>
          <p className='body-02 font-semibold text-red-700'>Could not reach the seeding function</p>
          <p className='body-01 mt-1 text-red-600'>{catalogueError}</p>
          <p className='body-01 mt-2 text-gray-600'>
            It refuses to run outside the dev project, and needs deploying alongside <code>handleEmailRequest</code>.
          </p>
        </div>
      </>
    );
  }

  if (!catalogue) {
    return (
      <>
        <PageHeader title='Delivery Request Trigger' />
        <div className='mt-10 flex justify-center'>
          <Spinner size={40} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title='Delivery Request Trigger'
        subtitle='Pushes fake NHS Delivery Requests through the real inbound email pipeline.'
      />
      <DevToolsNav />

      <div className='mt-6 overflow-y-auto pb-10'>
        <div className='mb-6 rounded border border-yellow-300 bg-yellow-50 p-3'>
          <p className='body-01 text-yellow-900'>
            <span className='font-semibold'>Development only.</span> Requests are parsed, geocoded, saved as transport
            jobs and pushed to drivers exactly as a real one would be. Target: <code>{catalogue.target}</code>
          </p>
        </div>

        <div className='mb-6'>
          {catalogue.demoBatchSize ? (
            <DemoBatchPanel size={catalogue.demoBatchSize} />
          ) : (
            <p className='body-01 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900'>
              The demo batch needs a newer <code>devSeedDeliveryRequests</code> than the one deployed. Redeploy it.
            </p>
          )}
        </div>

        <div className='rounded border p-5'>
          <p className='body-02 mb-4 font-semibold'>Single requests</p>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
            <div className='md:col-span-3'>
              <label className='body-01 mb-1 block font-semibold text-gray-700'>Scenario</label>
              <Select
                options={scenarioOptions}
                value={scenario}
                onChange={(option) => setScenario(option as IOption<string>)}
                placeholder='Choose a scenario'
              />
            </div>

            <div>
              <label className='body-01 mb-1 block font-semibold text-gray-700'>How many</label>
              <Textfield
                type='number'
                min={1}
                max={catalogue.maxCount}
                value={count}
                onChange={(event) => setCount((event.target as HTMLInputElement).value)}
              />
            </div>

            <div className='md:col-span-2'>
              <label className='body-01 mb-1 block font-semibold text-gray-700'>Email format</label>
              <Select
                options={FORMAT_OPTIONS}
                value={format}
                onChange={(option) => setFormat(option as IOption<DeliveryRequestEmailFormat>)}
              />
            </div>

            <div className='md:col-span-3'>
              {/* Missing when the deployed function is older than this page —
                  a deploy that half-succeeds leaves exactly that. */}
              {catalogue.options ? (
                <RequestFieldOverrides options={catalogue.options} onChange={setFieldOverrides} />
              ) : (
                <p className='body-01 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900'>
                  The deployed <code>devSeedDeliveryRequests</code> is older than this page, so it has no dropdown
                  options to offer. Redeploy it to get them back.
                </p>
              )}
            </div>

            <div className='md:col-span-3'>
              <label className='body-01 mb-1 block font-semibold text-gray-700'>
                More overrides (JSON, optional — applied on top of the dropdowns)
              </label>
              <TextArea
                minimumRows={3}
                value={overrides}
                onChange={(event) => setOverrides((event.target as HTMLTextAreaElement).value)}
                placeholder='{ "destinationSite": "Queens Hospital Burton", "urgent": true }'
              />
              {overridesError && <p className='body-01 mt-1 text-red-600'>{overridesError}</p>}
            </div>
          </div>

          <div className='mt-5 flex items-center gap-3'>
            <SharedButton
              type='button'
              appearance='primary'
              label='Send requests'
              isLoading={submitting}
              disabled={!scenario}
              onClick={() => submit(false)}
            />
            <SharedButton
              type='button'
              appearance='default'
              label='Preview email only'
              disabled={submitting || !scenario}
              onClick={() => submit(true)}
            />
          </div>
        </div>

        {results && (
          <div className='mt-6' data-testid='single-results'>
            <p className='body-02 mb-3 font-semibold'>
              {results.length} request{results.length === 1 ? '' : 's'}
              {wasDryRun ? ' (preview — nothing sent)' : ''}
            </p>
            {results.map((result) => (
              <div key={result.index} className='mb-3 rounded border p-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='body-02 font-semibold'>{result.route}</p>
                    <p className='body-01 text-gray-600'>
                      {result.requestType} · {result.requester}
                    </p>
                    <p className='body-01 text-gray-600'>Required {result.requiredAt}</p>
                  </div>
                  {result.status !== null && (
                    <Lozenge appearance={result.status === 200 ? 'success' : 'removed'}>HTTP {result.status}</Lozenge>
                  )}
                </div>
                {result.response && <p className='body-01 mt-2 text-gray-600'>{result.response}</p>}
                {result.emailText && (
                  <pre className='mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs'>
                    {result.emailText}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

        <div className='mt-6'>
          <ClearRequestsPanel />
        </div>
      </div>
    </>
  );
};

export default DeliveryRequestTrigger;
